import { faSackDollar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Alert from '@mui/material/Alert'
import { isAfter, isBefore, subMonths, startOfDay } from 'date-fns'
import type { FC } from 'react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import DeclaredIncome from '@/components/DeclaredIncome'
import H3 from '@/components/H3'
import { ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import RemoveButton from '@/components/RemoveButton'
import TrustedSourcesRequiredLabel from '@/components/TrustedSourcesRequiredLabel'
import VerifyButton from '@/components/VerifyButton'
import { IncomeValidationState, validationStateToMessage } from '@/types/Activity'
import type { Application, Income as IncomeT } from '@/types/Activity'
import type { OutcomeStatus } from '@/types/Outcome'
import { isStudentFinanceIncome } from '@/utils/income'

import AccountantReference from './AccountantReference'
import EmployerReference from './EmployerReference'
import EmploymentIncomeDetails from './EmploymentIncomeDetails'
import { ItemHeader } from './IncomeCard'
import IncomeTypeLabel from './IncomeTypeLabel'
import RemoveIncomeSidebar from './RemoveIncomeSidebar'
import StudentIncomeDetails from './StudentIncomeDetails'
import SupportingDocumentsDetails from './SupportingDocumentsDetails'
import TrustedSources from './TrustedSources'
import UnableToVerifyIncome from './UnableToVerifyIncome'
import { getReasonLabel } from './UnableToVerifyIncome/constants'
import { shouldShowTrustedSources } from './utils'

const IconWrapper = styled.span`
  margin-right: 10px;
`

export const doesIncomeSupportPayment = (income: IncomeT) => {
  const unsupportedIncomes = ['unemployment', 'up front', 'third party']

  const isStudentFinance = isStudentFinanceIncome(income.type, income.status)

  return !isStudentFinance && !unsupportedIncomes.includes(income.type.toLowerCase())
}

export const doesIncomeSupportDocuments = (income: IncomeT) => {
  const unsupportedIncomes = ['unemployment', 'up front', 'third party']

  const isStudentFinance = isStudentFinanceIncome(income.type, income.status)

  return !isStudentFinance && !unsupportedIncomes.includes(income.type.toLowerCase())
}

export const doesIncomeSupportUnableToVerify = (income: IncomeT) => {
  const unsupportedIncomes = ['unemployment', 'up front', 'third party']

  const isStudentFinance = isStudentFinanceIncome(income.type, income.status)

  return !isStudentFinance && !unsupportedIncomes.includes(income.type.toLowerCase())
}

export const isStartDateAfterThreeMonthsAgoAndBeforeToday = (startDate: string) => {
  const threeMonthsAgo = subMonths(new Date(), 3)

  const isStartDateAfterThreeMonthsAgo = isAfter(
    startOfDay(new Date(startDate)),
    threeMonthsAgo,
  )
  const isStartDateBeforeToday = isBefore(
    startOfDay(new Date(startDate)),
    startOfDay(new Date()),
  )

  return isStartDateAfterThreeMonthsAgo && isStartDateBeforeToday
}

const Container = styled.div`
  display: grid;
  gap: 20px;
`

interface Props {
  income: IncomeT
  incomeIndex: number
  handleIncomeVerifyClicked: (incomeIndex: number) => void
  application: Application
  handleImageClicked: (incomeIndex: number, imageIndex: number) => void
  canRemoveIncome: boolean
  customerId: string
  subjectId: string
  incomeOutcomeStatus?: OutcomeStatus
  onApplicantUpdated: () => void
  hasSingleIncome: boolean
  isRestrictedReference?: boolean
  onOpenRequests: () => void
}

const Income: FC<Props> = ({
  income,
  incomeIndex,
  handleIncomeVerifyClicked,
  application,
  handleImageClicked,
  canRemoveIncome,
  customerId,
  subjectId,
  incomeOutcomeStatus,
  onApplicantUpdated,
  hasSingleIncome,
  isRestrictedReference = false,
  onOpenRequests,
}) => {
  const {
    id,
    company,
    type,
    incomeProperties,
    guaranteedBonus,
    startDate,
    endDate,
    address,
    contractSchedule,
    contractType,
    evidenceType,
    position,
    status,
    supportingDocuments,
    validation,
    provider,
    idNumber,
    institution,
    taxReturnYearPeriod,
    directorPayType,
  } = income

  const history = useHistory()

  const [removeIncomePanelOpen, setRemoveIncomePanelOpen] = useState<boolean>(false)
  const unableToVerifyReason = validation?.unableToVerifyReason

  const navigateToEmployerReferee = (employerId: string) =>
    history.push(`/employer-referee/${employerId}`)

  const navigateToAccountantReferee = (accountantId: string) =>
    history.push(`/accountant-referee/${accountantId}`)

  return (
    <>
      {removeIncomePanelOpen && (
        <RemoveIncomeSidebar
          customerId={application.customerId}
          subjectId={application.subjectId}
          incomeId={id}
          onClose={() => setRemoveIncomePanelOpen(false)}
        />
      )}
      <Container>
        <div className="side-by-side-flex">
          <H3>
            <IconWrapper>
              <FontAwesomeIcon size="2x" icon={faSackDollar} />
            </IconWrapper>
            <IncomeTypeLabel income={income}>Income {incomeIndex + 1} - </IncomeTypeLabel>
          </H3>
          <ButtonsContainer>
            <VerifyButton
              disabled={isRestrictedReference}
              onClick={() => handleIncomeVerifyClicked(incomeIndex)}
            />
            {doesIncomeSupportUnableToVerify(income) && !isRestrictedReference && (
              <>
                {validation?.state !== IncomeValidationState.UnableToVerify && (
                  <UnableToVerifyIncome
                    customerId={application.customerId}
                    applicationId={application.applicationId}
                    subjectId={subjectId}
                    incomeId={income.id}
                    onSuccess={onApplicantUpdated}
                    mandatedTrustedSourcesEnabled={
                      application.journeyConfig?.mandatedTrustedSourcesEnabled
                    }
                    showTrustedSourcesRequiredLabel={income.trustedSourcesRequired}
                    incomeType={income.type}
                    incomeStatus={income.status}
                    companyName={income.company ?? ''}
                  />
                )}
              </>
            )}
            {canRemoveIncome && (
              <RemoveButton
                disabled={isRestrictedReference}
                onClick={() => setRemoveIncomePanelOpen(true)}
              />
            )}
          </ButtonsContainer>
        </div>
        <div>
          {income.trustedSourcesRequired && <TrustedSourcesRequiredLabel />}
          {type.toLowerCase() === 'employment' && !validation && (
            <p>
              Initial evidence for this employment will be via{' '}
              {evidenceType === 'Employer Reference' && 'a'}{' '}
              <strong>{evidenceType}</strong>
            </p>
          )}
          {type.toLowerCase() !== 'employment' && !validation && (
            <p>Initial evidence for this income will be via uploading documents</p>
          )}
          {validation && validation.state !== IncomeValidationState.ValidatedByOffice && (
            <p>{validationStateToMessage(validation.state)}</p>
          )}
          {validation && validation.state === IncomeValidationState.ValidatedByOffice && (
            <p>
              Applicant has been validated by{' '}
              <strong>{validation.validatorPerson?.name}</strong> (
              {validation.validatorPerson?.email})
            </p>
          )}
          {validation?.state === IncomeValidationState.UnableToVerify &&
            unableToVerifyReason && (
              <Alert severity="warning" sx={{ borderRadius: 2, my: 2 }}>
                <strong>Unable to verify reason:</strong>{' '}
                {getReasonLabel(unableToVerifyReason)}
              </Alert>
            )}
          <br />
          {doesIncomeSupportPayment(income) && (
            <div>
              <ItemHeader>
                Declared value of the {guaranteedBonus ? 'base' : ''} income
              </ItemHeader>
              <p>
                <DeclaredIncome incomeProperties={incomeProperties} />
              </p>
            </div>
          )}
          {guaranteedBonus && (
            <>
              <ItemHeader>Declared value of any Guaranteed Bonus</ItemHeader>
              <p>
                <DeclaredIncome incomeProperties={guaranteedBonus} />
              </p>
            </>
          )}
          {doesIncomeSupportPayment(income) && (
            <div>
              <ItemHeader>
                Confirmed value of the {guaranteedBonus ? 'base' : ''} income
              </ItemHeader>
              <p>
                {validation?.confirmed?.income ? (
                  <DeclaredIncome incomeProperties={validation?.confirmed?.income} />
                ) : (
                  <p>No confirmed income</p>
                )}
              </p>
            </div>
          )}
          {validation?.confirmed?.guaranteedBonus && (
            <>
              <ItemHeader>Confirmed value of any Guaranteed Bonus</ItemHeader>
              <p>
                {validation?.confirmed?.guaranteedBonus ? (
                  <DeclaredIncome
                    incomeProperties={validation?.confirmed?.guaranteedBonus}
                  />
                ) : (
                  <p>No confirmed bonus</p>
                )}
              </p>
            </>
          )}
          {directorPayType && (
            <div>
              <ItemHeader>Director Income Type</ItemHeader>
              <p>{directorPayType}</p>
            </div>
          )}
        </div>
        {type.toLowerCase() === 'pension' && (
          <>
            <div>
              {provider && (
                <>
                  <ItemHeader>Provider</ItemHeader>
                  <p>{provider}</p>
                </>
              )}
            </div>
          </>
        )}
        {type.toLowerCase() === 'employment' && (
          <>
            <EmploymentIncomeDetails
              company={company}
              position={position}
              contractType={contractType}
              address={address}
              contractSchedule={contractSchedule}
              startDate={startDate}
              endDate={endDate}
            />
            {(status.toLowerCase() === 'employed' ||
              status.toLowerCase() === 'employment') && (
              <EmployerReference
                customerId={customerId}
                subjectId={subjectId}
                income={income}
                navigateToEmployerReferee={navigateToEmployerReferee}
                incomeOutcomeStatus={incomeOutcomeStatus}
                applicantName={application.context?.name}
                isRestrictedReference={isRestrictedReference}
              />
            )}
            {status.toLowerCase() === 'self-employed' && (
              <>
                {taxReturnYearPeriod && (
                  <div>
                    <ItemHeader>Tax return year period</ItemHeader>
                    <p>{taxReturnYearPeriod}</p>
                  </div>
                )}
                <AccountantReference
                  customerId={customerId}
                  subjectId={subjectId}
                  incomeOutcomeStatus={incomeOutcomeStatus}
                  income={income}
                  navigateToAccountantReferee={navigateToAccountantReferee}
                  applicantName={application.context?.name}
                  isRestrictedReference={isRestrictedReference}
                />
              </>
            )}
          </>
        )}
        {type.toLocaleLowerCase() === 'student' && (
          <StudentIncomeDetails
            institution={institution}
            address={address}
            idNumber={idNumber}
            startDate={startDate}
            endDate={endDate}
            status={status}
          />
        )}
        {shouldShowTrustedSources(income) && (
          <TrustedSources
            openBankingConnections={income.openBankingConnections ?? []}
            konfirIntegration={income.konfirIntegration}
            incomeProperties={incomeProperties}
            incomeType={type}
            isShortEmployment={isStartDateAfterThreeMonthsAgoAndBeforeToday(startDate)}
            hasSingleIncome={hasSingleIncome}
            customerId={customerId}
            subjectId={subjectId}
            onOpenRequests={onOpenRequests}
          />
        )}
        {doesIncomeSupportDocuments(income) && (
          <SupportingDocumentsDetails
            supportingDocuments={supportingDocuments}
            incomeIndex={incomeIndex}
            handleImageClicked={handleImageClicked}
            documentBypassReason={income.documentBypassReason}
          />
        )}
      </Container>
    </>
  )
}

export default Income
